import { ThemeProvider } from '@emotion/react';
import { useRef } from 'react';
import { Typography } from 'src/components-v2/DataDisplay';
import BtnPrimary from 'src/components-v2/Inputs/Button/BtnPrimary';
import { Box, Column, Container, Row, Stack } from 'src/components-v2/Layout';
import { Image } from 'src/components-v2/Media';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import { useLazyGuilloche } from 'src/hooks/useLazyGuilloche';
import useWorkerGuilloche from 'src/hooks/useWorkerGuilloche';
import styled from 'threads5/themes/styled';
import type { ICareerSearchJob } from '../CareersSearch.types';

interface IJobDetailHeader {
  job: ICareerSearchJob;
}

const JobDetailHeader = ({ job }: IJobDetailHeader) => {
  const canvas1 = useRef<HTMLCanvasElement>(null);
  const { guilloche: donut, isGuillocheLoaded } = useLazyGuilloche({
    pattern: 'donut',
    canvasRef: canvas1,
  });
  useWorkerGuilloche({
    canvasRef: canvas1,
    svgPaths: donut?.paths,
    svgWidth: donut?.width,
    svgHeight: donut?.height,
    intensity: 0.2,
    speed: 0.1,
    horizontalWave: true,
    verticalWave: true,
    zAxisWave: true,
    id: 'hero-canvas',
    scale: { xs: 1.2, md: 1.2, xl: 1.5, xxl: 1.75 },
    y: { xs: -54, md: -50, xl: -50, xxl: -53 },
    x: { xs: -55, md: -50, xl: -75, xxl: -60 },
    disableAnimation: !isGuillocheLoaded,
  });

  const darkTheme = useComponentTheme('dark');

  return (
    <ThemeProvider theme={darkTheme}>
      <HeaderContainer>
        <GuillocheCanvas ref={canvas1} />
        <Box
          sx={{
            height: '100%',
            position: 'relative',
          }}
        >
          <BenImage
            src='https://images.ctfassets.net/ss5kfr270og3/5W0eh7vKWDArzqKagPpV4W/26f7a551bfc02558af5f29b32ab31757/ben-jobs-detail-hero.png?fm=webp&q=30&w=722'
            alt=''
            width={722}
            height={880}
            sources={{
              xs: `https://images.ctfassets.net/ss5kfr270og3/5W0eh7vKWDArzqKagPpV4W/26f7a551bfc02558af5f29b32ab31757/ben-jobs-detail-hero.png?fm=webp&q=30&w=244 1x, https://images.ctfassets.net/ss5kfr270og3/5W0eh7vKWDArzqKagPpV4W/26f7a551bfc02558af5f29b32ab31757/ben-jobs-detail-hero.png?fm=webp&q=30&w=488 2x`,
              sm: `https://images.ctfassets.net/ss5kfr270og3/5W0eh7vKWDArzqKagPpV4W/26f7a551bfc02558af5f29b32ab31757/ben-jobs-detail-hero.png?fm=webp&q=30&w=360 1x, https://images.ctfassets.net/ss5kfr270og3/5W0eh7vKWDArzqKagPpV4W/26f7a551bfc02558af5f29b32ab31757/ben-jobs-detail-hero.png?fm=webp&q=30&w=720 2x`,
              md: `https://images.ctfassets.net/ss5kfr270og3/5W0eh7vKWDArzqKagPpV4W/26f7a551bfc02558af5f29b32ab31757/ben-jobs-detail-hero.png?fm=webp&q=30&w=722 1x, https://images.ctfassets.net/ss5kfr270og3/5W0eh7vKWDArzqKagPpV4W/26f7a551bfc02558af5f29b32ab31757/ben-jobs-detail-hero.png?fm=webp&q=30&w=1444 2x`,
            }}
          />
          <HeroHoloStrip
            src='https://images.ctfassets.net/ss5kfr270og3/7q1mJPw6bmeeUruloUUcxa/d69826c7922a24d66660ccd4e732a79a/3621384b921832f85bca12c7d2a4f5da?fm=webp&fit=crop&f=top_left&w=20&h=1200&q=30'
            alt=''
            width={20}
            height={600}
            sources={{
              xs: 'https://images.ctfassets.net/ss5kfr270og3/7q1mJPw6bmeeUruloUUcxa/d69826c7922a24d66660ccd4e732a79a/3621384b921832f85bca12c7d2a4f5da?fm=webp&fit=crop&f=center&w=639&h=14&q=30 1x, https://images.ctfassets.net/ss5kfr270og3/7q1mJPw6bmeeUruloUUcxa/d69826c7922a24d66660ccd4e732a79a/3621384b921832f85bca12c7d2a4f5da?fm=webp&fit=crop&f=center&w=1278&h=28&q=30 2x',
              sm: 'https://images.ctfassets.net/ss5kfr270og3/7q1mJPw6bmeeUruloUUcxa/d69826c7922a24d66660ccd4e732a79a/3621384b921832f85bca12c7d2a4f5da?fm=webp&fit=crop&f=top_left&w=14&h=1200&q=30 1x, https://images.ctfassets.net/ss5kfr270og3/7q1mJPw6bmeeUruloUUcxa/d69826c7922a24d66660ccd4e732a79a/3621384b921832f85bca12c7d2a4f5da?fm=webp&fit=crop&f=top_left&w=28&h=2400&q=30 2x',
              md: 'https://images.ctfassets.net/ss5kfr270og3/7q1mJPw6bmeeUruloUUcxa/d69826c7922a24d66660ccd4e732a79a/3621384b921832f85bca12c7d2a4f5da?fm=webp&fit=crop&f=top_left&w=20&h=1200&q=30 1x, https://images.ctfassets.net/ss5kfr270og3/7q1mJPw6bmeeUruloUUcxa/d69826c7922a24d66660ccd4e732a79a/3621384b921832f85bca12c7d2a4f5da?fm=webp&fit=crop&f=top_left&w=40&h=2400&q=30 2x',
            }}
          />
          <Container
            sx={{
              paddingTop: {
                xs: '80px',
                sm: '84px',
                md: '95px',
              },
              height: '100%',
              zIndex: 10,
              position: 'relative',
              ml: {
                xl: 'calc(50vw - 686px - 45px)',
              },
            }}
          >
            <Row sx={{ height: '100%', m: '0' }}>
              <Column xs={24} lg={21} sx={{ height: '100%', py: '0' }}>
                <Stack
                  justifyContent='center'
                  gap={{ xs: 3, md: 6 }}
                  sx={{
                    height: '100%',
                    my: {
                      xs: 8,
                      md: '0',
                    },
                  }}
                >
                  <Stack gap={{ xs: 3, md: 4 }}>
                    <Typography variant='h2' component='h1'>
                      {job.text}
                    </Typography>
                    <HeaderInfoBar
                      items={[job.department, job.location, job.commitment]}
                    />
                  </Stack>
                  <BtnPrimary
                    href={job.applyUrl}
                    sx={{
                      width: {
                        xs: '145px',
                        md: '200px',
                      },
                      justifyContent: 'center',
                    }}
                  >
                    Apply
                  </BtnPrimary>
                </Stack>
              </Column>
            </Row>
          </Container>
        </Box>
      </HeaderContainer>
    </ThemeProvider>
  );
};

const HeaderContainer = styled('div')(({ theme }) => {
  return {
    position: 'relative',
    color: theme.palette['base/white'],
    background:
      'linear-gradient(33deg,  #02294B 0.48%,  #07578D 59.62%, #0B7BBC 100%)',
    [theme.breakpoints.up('md')]: {
      height: '100vh',
      position: 'sticky',
      top: 0,
    },
  };
});

const HeaderInfo = styled(Stack)(({ theme }) => {
  return {
    fontFamily: theme.typography.cern,
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '112%',
    color: theme.palette['base/sky/200'],
    textTransform: 'uppercase',
    [theme.breakpoints.up('md')]: {
      fontSize: '16px',
    },
  };
});

const HeaderInfoSpacer = styled('div')(({ theme }) => {
  return {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
      width: '2px',
      backgroundColor: theme.palette['base/sky/600'],
      height: '100%',
    },
  };
});

const HeaderInfoBar = ({ items }) => {
  const cleanItems = items?.filter((text) => {
    return !!text;
  });
  const itemsWithSpacers = cleanItems.flatMap((item, ix) => {
    return ix < cleanItems.length - 1
      ? [
          <div key={item}>{item}</div>,
          <HeaderInfoSpacer key={`spacer-${ix}`} />,
        ]
      : [<div key={item}>{item}</div>];
  });

  return (
    <HeaderInfo
      direction={{
        xs: 'column',
        md: 'row',
      }}
      gap={2}
      alignItems={{
        xs: 'start',
        md: 'center',
      }}
    >
      {itemsWithSpacers}
    </HeaderInfo>
  );
};

const BenImage = styled(Image)(({ theme }) => {
  return {
    position: 'absolute',
    bottom: 0,
    right: 0,
    zIndex: 1,
    width: '244px',
    [theme.breakpoints.up('sm')]: {
      width: '360px',
    },
    [theme.breakpoints.up('md')]: {
      width: '722px',
    },
  };
});

const HeroHoloStrip = styled(Image)(({ theme }) => {
  return {
    position: 'absolute',
    zIndex: 0,
    opacity: 0.8,
    width: '100%',
    height: '14px',
    bottom: '20px',
    right: 0,
    [theme.breakpoints.up('sm')]: {
      height: '100%',
      width: '14px',
      top: 0,
      right: '5vw',
    },
    [theme.breakpoints.up('md')]: {
      height: '100%',
      width: '20px',
      top: 0,
      right: '5vw',
    },
  };
});

const GuillocheCanvas = styled('canvas')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
});

export default JobDetailHeader;
